import { useCallback, useState } from 'react';

import { useParams } from 'react-router-dom';

import { AddFactDialog } from './dialogs/add-fact-dialog';
import { ArchiveSelectedButton } from './dialogs/archive-selected';
import { FilterFactsDialog } from './dialogs/filter-facts-dialog';
import { UpdateRelevanceButton } from './dialogs/update-relevance';
import { MatterRunsGetResponseDto } from '../../../../../../../../../../backend/src/matter/dto/matter-runs-get.response.dto';
import { RunV2 } from '../../../../../../../../../../backend/src/run/run.entity';
import { useMatterProcessingStore } from '../../../../../../../stores/matter-processing-store';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetCaseDocTypes } from '@/api/queries/useGetCaseDocTypes';
import { useGetCaseThemes } from '@/api/queries/useGetCaseThemes';
import { Person, Run } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { FactChronologyDropdown } from '@/new/components/fact-chronology-dropdown';
import { Icon } from '@/new/components/icon';
import { ClusterFactsModal } from '@/new/components/labeller/cluster-facts';
import { CreateInconsistencyModal } from '@/new/components/labeller/create-inconsistency';
import { NaturalLanguageSearchBox } from '@/new/components/natural-language-search-bar';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SubsetChip } from '@/new/components/subset-chip';
import { FactFilters as FactFiltersType } from '@/types';

interface FactsEditorToolbarProps {
  globalFilter: string;
  refetchCaseFacts: () => void;
  clearSearch: () => void;
  setGlobalFilter: (value: string) => void;
  onSearchCall: (searchValue?: string, useNaturalLanguage?: boolean) => void;
  goToPage: (value: number) => void;
  activeFilters: FactFiltersType;
  setFilters: (v: FactFiltersType) => void;
  caseRuns: RunV2[];
  allPersons: Person[];
  onClusterComplete: () => void;
  processingNL: boolean;
  filterCount: number;
  selectedFactIds: Set<string>;
  setSelectedFactIds: (ids: Set<string>) => void;
  totalUnfilteredCount?: number;
}

export const FactsTableHeader = ({
  globalFilter,
  selectedFactIds,
  refetchCaseFacts,
  clearSearch,
  setGlobalFilter,
  onSearchCall,
  goToPage,
  activeFilters,
  setFilters,
  caseRuns,
  allPersons,
  onClusterComplete,
  processingNL,
  filterCount,
  setSelectedFactIds,
  totalUnfilteredCount,
}: FactsEditorToolbarProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) {
    return;
  }

  const { data: themesWithCounts = [] } = useGetCaseThemes({ matterId, subsetId });
  const { data: docTypes = [] } = useGetCaseDocTypes({ matterId, subsetId });

  const caseThemes = themesWithCounts.map((theme) => theme.name);

  // State
  const [factAdderModalIsOpen, setFactAdderModalIsOpen] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [clusterModalOpen, setClusterModalOpen] = useState(false);
  const [createInconsistencyModalOpen, setCreateInconsistencyModalOpen] = useState(false);

  const { user } = useUserContext();
  const isLabellingOrg = user?.organisation?.isLabellingOrg ?? false;

  const handleNewFactCreated = useCallback(() => {
    trackCustomEvent(TrackingEventsV2.AddFact, { matterId });
    refetchCaseFacts();
    setFactAdderModalIsOpen(false);
  }, [refetchCaseFacts, matterId]);

  const resultsIncludedLength = selectedFactIds.size;

  const { getProcessingState } = useMatterProcessingStore();

  return (
    <>
      <PageHeader>
        <div className="flex items-center gap-4">
          <PageTitle text="Facts" />
          <SubsetChip subsetId={subsetId} className="mt-0.5" />
        </div>

        <div className="flex items-center gap-2">
          <FactChronologyDropdown
            matterId={matterId}
            subsetId={subsetId}
            selectedFactIds={selectedFactIds}
            variant="primary"
          />

          {isLabellingOrg && (
            <>
              <Button disabled={resultsIncludedLength < 2} onClick={() => setClusterModalOpen(true)}>
                <Icon className="lucide--group" />
                Create Cluster
              </Button>

              <Button disabled={resultsIncludedLength < 2} onClick={() => setCreateInconsistencyModalOpen(true)}>
                <Icon className="lucide--triangle-alert" />
                Create Inconsistency
              </Button>
            </>
          )}

          <ButtonGroup>
            <UpdateRelevanceButton selectedFactIds={selectedFactIds} />
            <ArchiveSelectedButton selectedFactIds={selectedFactIds} setSelectedFactIds={setSelectedFactIds} />
          </ButtonGroup>

          <Button
            variant="default"
            onClick={() => setFactAdderModalIsOpen(true)}
            disabled={!getProcessingState(matterId)?.hasCompletedRuns}
          >
            <Icon className="lucide--plus" />
            Add Fact
          </Button>

          <AddFactDialog
            isOpen={factAdderModalIsOpen}
            onOpenChange={setFactAdderModalIsOpen}
            handleNewFactCreated={handleNewFactCreated}
          />
        </div>
      </PageHeader>

      <div className="flex w-full gap-2">
        <NaturalLanguageSearchBox
          value={globalFilter}
          onChange={setGlobalFilter}
          onSearchCall={onSearchCall}
          clearSearch={clearSearch}
          processingNL={processingNL}
          entityType="facts"
        />

        <ButtonGroup>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={activeFilters.disputedFacts ? 'destructive' : 'default'}
                onClick={() => {
                  setFilters({
                    ...activeFilters,
                    disputedFacts: !activeFilters.disputedFacts,
                  });
                  goToPage(0);
                }}
                className={activeFilters.keyFacts ? 'border-r-0!' : ''}
              >
                <Icon className="lucide--triangle-alert" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle disputed Facts</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={activeFilters.keyFacts ? 'warn' : 'default'}
                onClick={() => {
                  setFilters({
                    ...activeFilters,
                    keyFacts: !activeFilters.keyFacts,
                  });
                  goToPage(0);
                }}
                className={activeFilters.keyFacts ? 'z-10 not-hover:border-l!' : ''}
              >
                <Icon className="lucide--key-round" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle key Facts</TooltipContent>
          </Tooltip>
        </ButtonGroup>

        <FilterFactsDialog
          isOpen={filtersModalOpen}
          onOpenChange={setFiltersModalOpen}
          filterCount={filterCount}
          activeFilters={activeFilters}
          allDocumentTypes={docTypes}
          setFilters={setFilters}
          caseRuns={caseRuns}
          caseThemes={caseThemes}
          allPersons={allPersons}
          goToPage={goToPage}
          totalUnfilteredCount={totalUnfilteredCount}
        />
      </div>

      {clusterModalOpen && (
        <ClusterFactsModal
          isOpen={clusterModalOpen}
          onClose={() => setClusterModalOpen(false)}
          matterId={matterId}
          subsetId={subsetId}
          selectedFactIds={selectedFactIds}
          onSuccess={() => {
            setClusterModalOpen(false);
            onClusterComplete();
          }}
        />
      )}
      {createInconsistencyModalOpen && (
        <CreateInconsistencyModal
          isOpen={createInconsistencyModalOpen}
          onClose={() => setCreateInconsistencyModalOpen(false)}
          caseId={matterId}
          onSuccess={() => {
            setCreateInconsistencyModalOpen(false);
            refetchCaseFacts();
          }}
        />
      )}
    </>
  );
};
